import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Search, Users, Phone, Mail, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CRMCustomers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCustomers(); }, [search]);

  const fetchCustomers = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const params = search ? { search } : {};
      const res = await axios.get(`${API}/customers`, { headers, params });
      setCustomers(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  return (
    <div data-testid="crm-customers">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Kunden</h1>
        <span className="text-sm text-slate-500">{customers.length} Kunden</span>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="customer-search"
          placeholder="Kunden suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12 rounded-xl bg-white border-slate-200 text-base"
        />
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32"><div className="animate-spin h-6 w-6 border-3 border-cyan-700 border-t-transparent rounded-full" /></div>
        ) : customers.length === 0 ? (
          <p className="text-sm text-slate-400 p-6 text-center">Noch keine Kunden vorhanden</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {customers.map((c) => (
              <div
                key={c.id}
                data-testid={`customer-row-${c.id}`}
                onClick={() => navigate(`/app/customers/${c.id}`)}
                className="flex items-center justify-between p-4 md:p-5 hover:bg-slate-50 transition-colors cursor-pointer group active:bg-slate-100"
              >
                <div className="flex items-center gap-3 md:gap-4 min-w-0">
                  <div className="h-11 w-11 bg-cyan-50 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-cyan-700">{(c.name || "?")[0].toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm md:text-base font-semibold text-slate-800 truncate">{c.name}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="flex items-center gap-1 text-xs text-slate-500 truncate"><Mail className="h-3 w-3 shrink-0" />{c.email}</span>
                      {c.phone && <span className="flex items-center gap-1 text-xs text-slate-500 hidden sm:flex"><Phone className="h-3 w-3 shrink-0" />{c.phone}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:gap-4 shrink-0">
                  {c.pflegegrad && (
                    <span className="text-xs font-medium bg-cyan-50 text-cyan-700 px-2.5 py-1.5 rounded-full">PG {c.pflegegrad}</span>
                  )}
                  <span className="text-xs text-slate-400 hidden sm:inline">{c.appointment_count || 0} Termine</span>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-cyan-600 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
